import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import {
  Shield, LayoutDashboard, Users, UserCheck, Eye, FileSearch, AlertCircle,
  FileText, Settings, LogOut, Bell, Key, Activity, FileWarning,
  Network, BarChart3, Sun, Moon, Menu, X, Newspaper, Building2, Scale, Globe, Briefcase, Gavel, Database, Link2, Webhook, IdCard, ScanFace, Video, Workflow, Fingerprint, ShieldAlert, BrainCircuit, ShieldOff, Bitcoin, TrendingUp, ShieldCheck, Zap, Sparkles, Brain, ShieldQuestion, BookUser, Receipt, HeartPulse, FileBadge2, Landmark
} from "lucide-react";
import { SLABreachBell } from "./dashboard/SLABreachBell";

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close mobile sidebar on route change (Feb-2026 mobile fix).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Plan gating — Basic users only see PAN + Aadhaar (DigiLocker).
  // Everything else stays under Advance (coming soon).
  const BASIC_ALLOWED_PATHS = new Set([
    "/dashboard",
    "/pan-verify",
    "/aadhaar-verify",
    "/settings",
    "/profile",
  ]);
  const userPlan = String(user?.plan || "").toLowerCase();
  const isBasic = userPlan === "basic";

  const navGroups = [
    {
      title: "Overview",
      items: [
        { path: "/copilot", icon: Brain, label: "Compliance Copilot", badge: "NEW" },
        { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/customers", icon: Users, label: "Customers" },
        { path: "/onboarding-tokens", icon: Link2, label: "Onboarding" },
        { path: "/cases", icon: AlertCircle, label: "Cases" },
      ],
    },
    {
      title: "Identity Verification",
      items: [
        { path: "/pan-verify", icon: IdCard, label: "PAN Verify" },
        { path: "/aadhaar-verify", icon: Fingerprint, label: "Aadhaar (DigiLocker)" },
        { path: "/ckyc", icon: BookUser, label: "CKYC 2.0", badge: "NEW" },
        { path: "/gst-lookup", icon: Building2, label: "GSTIN Verify" },
        { path: "/india-entity", icon: Landmark, label: "LLP / MSME / Firm", badge: "NEW" },
        { path: "/company-verify", icon: Building2, label: "Company (MCA)", badge: "NEW" },
        { path: "/face-match", icon: ScanFace, label: "Face Match" },
        { path: "/liveness", icon: ShieldCheck, label: "Liveness / PAD", badge: "NEW" },
        { path: "/vkyc", icon: Video, label: "Video KYC" },
        { path: "/journey", icon: Workflow, label: "KYC+AML Journey" },
        { path: "/verify/cases", icon: FileBadge2, label: "Verify Cases" },
      ],
    },
    {
      title: "AML",
      items: [
        { path: "/entity-screening", icon: Sparkles, label: "Entity Risk Screening", badge: "NEW" },
        { path: "/360-search", icon: Network, label: "360° Search" },
        { path: "/opensanctions", icon: Globe, label: "Sanctions Watch" },
        { path: "/fuzzy-match", icon: Zap, label: "Fuzzy Match" },
        { path: "/adverse-media", icon: Newspaper, label: "Adverse Media" },
        { path: "/adverse-media/precision", icon: ShieldQuestion, label: "Precision Screen", badge: "NEW", roles: ["super_admin", "compliance_officer"] },
        { path: "/screening", icon: FileSearch, label: "Screening" },
        { path: "/str-generator", icon: FileWarning, label: "STR Generator" },
      ],
    },
    {
      title: "Fraud",
      items: [
        { path: "/fraud-detect", icon: ShieldAlert, label: "Fraud Detection" },
        { path: "/behavioral", icon: BrainCircuit, label: "Behavioral Analysis" },
        { path: "/ato", icon: ShieldOff, label: "ATO Detection" },
        { path: "/fraud-network", icon: Network, label: "Fraud Network" },
        { path: "/transactions", icon: Activity, label: "Transactions" },
      ],
    },
    {
      title: "India Intel",
      items: [
        { path: "/kyb/one-click", icon: Sparkles, label: "KYB One-Click", badge: "NEW" },
        { path: "/mca21", icon: Briefcase, label: "MCA21 Registry" },
        { path: "/mca21/directors", icon: UserCheck, label: "Director Search" },
        { path: "/mca21/din-watchlist", icon: Eye, label: "DIN Watchlist" },
        { path: "/enforcement", icon: Gavel, label: "ED/CBI/SFIO" },
        { path: "/court-records", icon: Scale, label: "Court Records" },
        { path: "/watchlist", icon: Database, label: "Watchlist DB" },
        { path: "/crypto-screen", icon: Bitcoin, label: "Crypto Screen" },
        { path: "/entity-intelligence", icon: Network, label: "Entity Intel" },
      ],
    },
    {
      title: "Platform",
      items: [
        { path: "/reports", icon: BarChart3, label: "Reports" },
        { path: "/cbill", icon: Receipt, label: "Cbill · Usage", badge: "NEW" },
        { path: "/system-health", icon: HeartPulse, label: "System Health", badge: "NEW" },
        { path: "/developers", icon: Key, label: "API Keys" },
        { path: "/webhooks", icon: Webhook, label: "Webhooks" },
        { path: "/audit-logs", icon: FileText, label: "Audit Logs" },
        { path: "/subscription", icon: TrendingUp, label: "Subscription" },
        { path: "/settings", icon: Settings, label: "Settings" },
        ...(user?.role === "super_admin"
          ? [{ path: "/platform-admin", icon: ShieldCheck, label: "Platform Admin" }]
          : []),
      ],
    },
  ];
  // Persisted collapse state per group
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = sessionStorage.getItem("rudrik_sidebar_collapsed");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleGroup = (title) => {
    setCollapsed(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try { sessionStorage.setItem("rudrik_sidebar_collapsed", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
    {/* Mobile hamburger button */}
    <button
      onClick={() => setMobileOpen(true)}
      data-testid="mobile-menu-btn"
      className="sidebar-mobile-overlay"
      style={{
        display: "none", position: "fixed", top: "12px", left: "12px", zIndex: 60,
        background: "#0d1117", border: "1px solid #1e2530", borderRadius: "8px",
        padding: "8px", cursor: "pointer", color: "#f1f5f9",
      }}
    >
      <Menu style={{ width: "20px", height: "20px" }} />
    </button>

    {/* Mobile overlay */}
    {mobileOpen && (
      <div onClick={() => setMobileOpen(false)} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 55,
      }} />
    )}

    <div className={`sidebar-desktop${mobileOpen ? " mobile-open" : ""}`} style={{
      width: "220px",
      background: "var(--bg-card, #0d1117)",
      borderRight: "1px solid var(--border-color, #1e2530)",
      height: "100vh",
      position: "fixed",
      left: mobileOpen ? 0 : undefined,
      top: 0,
      display: "flex",
      flexDirection: "column",
      zIndex: 56,
      transition: "left 0.2s ease",
    }}>
      {/* Mobile close-X button (only visible <md) */}
      <button
        onClick={() => setMobileOpen(false)}
        data-testid="sidebar-close-btn"
        className="sidebar-mobile-close"
        aria-label="Close sidebar"
        style={{
          display: "none", position: "absolute", top: "8px", right: "8px", zIndex: 60,
          background: "transparent", border: "1px solid #1e2530", borderRadius: "8px",
          padding: "6px", cursor: "pointer", color: "#f1f5f9",
        }}
      >
        <X style={{ width: "16px", height: "16px" }} />
      </button>
      {/* Logo + Theme Toggle */}
      <div className="flex items-center justify-between" style={{ padding: "16px 16px 0" }}>
        <a href="https://rudrik.io" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }} data-testid="sidebar-logo-link">
          <img
            src="/assets/rudrik-logo-full.png"
            alt="Rudrik Technology Pvt Ltd"
            style={{ height: "44px", width: "auto", objectFit: "contain", display: "block", filter: "drop-shadow(0 2px 8px rgba(37, 99, 235, 0.3))" }}
          />
        </a>
        <button onClick={toggleTheme} data-testid="theme-toggle" style={{
          background: "transparent", border: "1px solid var(--border-color, #1e2530)", borderRadius: "8px",
          padding: "6px", cursor: "pointer", color: "var(--text-secondary, #94a3b8)",
        }}>
          {theme === "dark" ? <Sun style={{ width: "14px", height: "14px" }} /> : <Moon style={{ width: "14px", height: "14px" }} />}
        </button>
      </div>

      {/* Navigation */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 0"
      }}>
        {/* Quick search (Cmd/Ctrl+K) */}
        <button
          data-testid="sidebar-quicksearch-btn"
          onClick={() => window.dispatchEvent(new Event("rudrik:open-quicksearch"))}
          style={{
            width: "calc(100% - 32px)",
            margin: "0 16px 12px",
            background: "linear-gradient(135deg, rgba(168,85,247,0.12), rgba(124,58,237,0.05))",
            border: "1px solid #a855f755",
            borderRadius: 8,
            padding: "8px 12px",
            color: "#cbd5e1",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <FileSearch size={13} style={{ color: "#a855f7" }} /> Quick Search
          </span>
          <kbd style={{ fontSize: 9, padding: "2px 6px", background: "#080c12", border: "1px solid #1e2530", borderRadius: 3, fontFamily: "ui-monospace, monospace", color: "#94a3b8" }}>⌘K</kbd>
        </button>

        {navGroups
          .map((group) => ({
            ...group,
            items: group.items.filter((i) =>
              !isBasic || BASIC_ALLOWED_PATHS.has(i.path)),
          }))
          .filter((group) => group.items.length > 0)
          .map((group) => {
          const isCollapsed = !!collapsed[group.title];
          const groupHasActive = group.items.some(i => location.pathname === i.path);
          return (
            <div key={group.title} style={{ marginBottom: 6 }} data-testid={`nav-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <button
                type="button"
                onClick={() => toggleGroup(group.title)}
                data-testid={`nav-group-toggle-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px 20px 6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: groupHasActive ? "#2563eb" : "#475569",
                  fontSize: "10px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  textAlign: "left",
                }}
              >
                <span>{group.title}</span>
                <span style={{
                  fontSize: 9,
                  transition: "transform 0.2s ease",
                  transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                }}>▼</span>
              </button>
              {!isCollapsed && group.items
                .filter((item) => !item.roles || item.roles.includes(String(user?.role || "").toLowerCase()))
                .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "8px 20px 8px 28px",
                      color: isActive ? "#f1f5f9" : "#94a3b8",
                      textDecoration: "none",
                      fontSize: "13px",
                      fontWeight: isActive ? "600" : "400",
                      borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent",
                      background: isActive ? "linear-gradient(90deg, #1e3a5f, #1e2d45)" : "transparent",
                      transition: "background 0.2s ease, color 0.2s ease",
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "#1e2530";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span style={{
                        marginLeft: "auto", fontSize: 9, fontWeight: 700,
                        padding: "2px 6px", borderRadius: 999,
                        background: "rgba(59,130,246,0.18)",
                        border: "1px solid rgba(59,130,246,0.40)",
                        color: "#93c5fd", letterSpacing: 0.5,
                      }}>{item.badge}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Plan badge (Basic users see an upgrade prompt) */}
      {isBasic && (
        <div data-testid="plan-badge-basic"
             style={{ margin: "10px 16px 0", padding: 12, borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(37,99,235,0.14), rgba(37,99,235,0.04))",
                        border: "1px solid #2563eb55" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <ShieldCheck size={11} style={{ color: "#60a5fa" }} />
            <span style={{ color: "#60a5fa", fontSize: 10, fontWeight: 800,
                             textTransform: "uppercase", letterSpacing: 0.6 }}>
              Plan · Basic
            </span>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 10, lineHeight: 1.5, marginBottom: 8 }}>
            Aadhaar + PAN verification only. Full AML, MCA, Video KYC and more coming with Advance.
          </div>
          <button type="button" disabled
                  data-testid="plan-upgrade-btn"
                  style={{ width: "100%", padding: "6px 10px", borderRadius: 6,
                             background: "rgba(245,158,11,0.15)",
                             border: "1px solid #f59e0b55",
                             color: "#f59e0b", fontSize: 10, fontWeight: 800,
                             cursor: "not-allowed",
                             textTransform: "uppercase", letterSpacing: 0.6 }}>
            Advance · Coming Soon
          </button>
        </div>
      )}

      {/* User Profile */}
      <div style={{
        borderTop: "1px solid #1e2530",
        padding: "16px 20px"
      }}>
        <div className="flex items-center justify-between mb-3">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#f1f5f9",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>{user?.name || "User"}</div>
            <div style={{
              fontSize: "11px",
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>{user?.role?.replace("_", " ")}</div>
          </div>
          <div className="flex items-center gap-2">
            <SLABreachBell />
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              style={{
                background: "transparent",
                border: "1px solid #1e2530",
                borderRadius: "6px",
                padding: "6px",
                cursor: "pointer",
                color: "#94a3b8",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#1e2530";
                e.currentTarget.style.color = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#94a3b8";
              }}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 20px", borderTop: "1px solid #1e2530" }}>
        <div style={{ fontSize: "9px", color: "#334155", textAlign: "center", lineHeight: "1.4" }}>
          &copy; 2026 Rudrik Technologies Pvt. Ltd.
        </div>
        <a
          href="https://rudrik.io"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-website-link"
          style={{
            display: "block",
            fontSize: "9px",
            color: "#475569",
            textAlign: "center",
            marginTop: "4px",
            textDecoration: "none",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#2563eb"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#475569"; }}
        >
          www.rudrik.io
        </a>
      </div>
    </div>
    </>
  );
}
